import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Input, Row, Select, Space, Spin, Typography, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useQueryClient } from '@tanstack/react-query';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatLogInvoiceLines, useGetMatLogInvoiceSummary } from '../../../hooks/genericHooks';
import { fetchSqlDataWithQuery } from '../../../services/api';
import type { MatLogInvoiceLine, MatLogInvoiceSummary } from '../../../types';
import Button from '../../../components/common/Button';

const { Text } = Typography;

const normalizeStr = (v: any) => String(v ?? '').trim();

const unwrapRows = <T,>(resp: any): T[] => {
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
};

const formatNum = (v: any, decimals = 2) => {
  const n = typeof v === 'number' ? v : Number(String(v));
  if (!Number.isFinite(n)) return '';
  return n.toFixed(decimals);
};

const InvoiceLinesTable: React.FC<{
  shipmentId: string;
  invoiceNo?: string | null;
  currency?: string | null;
  lineSearch?: string;
}> = ({
  shipmentId,
  invoiceNo,
  currency,
  lineSearch,
}) => {
  const { data, isLoading, error } = useGetMatLogInvoiceLines({
    shipment_id: shipmentId,
    invoice_no: invoiceNo ?? null,
    currency: currency ?? null,
  }) as any;
  const rowsAll: MatLogInvoiceLine[] = unwrapRows<MatLogInvoiceLine>(data);

  const rows: MatLogInvoiceLine[] = useMemo(() => {
    const query = normalizeStr(lineSearch).toLowerCase();
    if (!query) return rowsAll;
    const terms = query.split(/\s+/).filter(Boolean);
    if (!terms.length) return rowsAll;
    return (rowsAll || []).filter((r: any) => {
      const hay = [r?.itemcode, r?.parent_item_code, r?.description]
        .map((v) => normalizeStr(v).toLowerCase())
        .join(' ');
      return terms.every((t) => hay.includes(t));
    });
  }, [lineSearch, rowsAll]);

  const columns: ColumnType<MatLogInvoiceLine>[] = useMemo(
    () => [
      {
        title: 'Parent',
        dataIndex: 'parent_item_code',
        key: 'parent_item_code',
        width: 120,
        render: (v) => {
          const s = normalizeStr(v);
          return s ? <Text code>{s}</Text> : '';
        },
      },
      {
        title: 'Item Code',
        dataIndex: 'itemcode',
        key: 'itemcode',
        width: 120,
        render: (v) => {
          const s = normalizeStr(v);
          return s ? <Text code>{s}</Text> : '';
        },
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 280,
        render: (v) => normalizeStr(v),
      },
      { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70, render: (v) => normalizeStr(v) },
      {
        title: 'Unit Price',
        dataIndex: 'unit_price',
        key: 'unit_price',
        width: 95,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => formatNum(v, 3),
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 80,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => formatNum(v, 3),
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 110,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => formatNum(v, 3),
      },
      {
        title: 'Pkgs',
        dataIndex: 'package_count',
        key: 'package_count',
        width: 60,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => (v == null || v === '' ? '' : String(v)),
      },
    ],
    []
  );

  if (error) {
    return <Alert type="error" showIcon message="Failed to load invoice lines" style={{ margin: 8 }} />;
  }

  return (
    <div style={{ paddingLeft: 8, paddingRight: 8, background: '#e6f4ff', borderRadius: 6, paddingTop: 8, paddingBottom: 8 }}>
      <Spin spinning={isLoading}>
        {!isLoading && (!rows || rows.length === 0) && (
          <Alert
            type="warning"
            showIcon
            message="No invoice line items found"
            description={`shipment_id=${normalizeStr(shipmentId)} invoice_no=${normalizeStr(invoiceNo)} currency=${normalizeStr(currency)}`}
            style={{ marginBottom: 8 }}
          />
        )}
        <StandardTable
          enableExport={false}
          enableColumnTools={false}
          enableResetView={false}
          columns={columns as any}
          dataSource={rows}
          rowKey={(r: any) => `${shipmentId}::${normalizeStr((r as any)?.itemcode)}::${normalizeStr((r as any)?.unit_price)}::${normalizeStr((r as any)?.currency)}::${normalizeStr((r as any)?.unit)}`}
          pagination={false}
          size="small"
        />
      </Spin>
    </div>
  );
};

const MatLogInvoices: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useGetMatLogInvoiceSummary() as any;
  const summaries: MatLogInvoiceSummary[] = unwrapRows<MatLogInvoiceSummary>(data);

  const [q, setQ] = useState('');
  const [shipmentFilter, setShipmentFilter] = useState<string | undefined>(undefined);
  const [invoiceFilter, setInvoiceFilter] = useState<string | undefined>(undefined);
  const [currencyFilter, setCurrencyFilter] = useState<string | undefined>(undefined);
  const [lineQ, setLineQ] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const shipmentOptions = useMemo(() => {
    const uniq = new Set<string>();
    (summaries || []).forEach((r) => {
      const v = normalizeStr((r as any)?.shipment_id);
      if (v) uniq.add(v);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [summaries]);

  const invoiceOptions = useMemo(() => {
    const uniq = new Set<string>();
    (summaries || []).forEach((r) => {
      const v = normalizeStr((r as any)?.invoice_no);
      if (v) uniq.add(v);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [summaries]);

  const currencyOptions = useMemo(() => {
    const uniq = new Set<string>();
    (summaries || []).forEach((r) => {
      const v = normalizeStr((r as any)?.currency);
      if (v) uniq.add(v);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [summaries]);

  const filtered = useMemo(() => {
    let out = summaries || [];

    if (shipmentFilter) {
      out = out.filter((r) => normalizeStr((r as any)?.shipment_id) === normalizeStr(shipmentFilter));
    }
    if (invoiceFilter) {
      out = out.filter((r) => normalizeStr((r as any)?.invoice_no) === normalizeStr(invoiceFilter));
    }
    if (currencyFilter) {
      out = out.filter((r) => normalizeStr((r as any)?.currency) === normalizeStr(currencyFilter));
    }

    const query = normalizeStr(q).toLowerCase();
    if (query) {
      const terms = query.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const hay = [
          (r as any)?.shipment_id,
          (r as any)?.invoice_no,
          (r as any)?.currency,
          (r as any)?.total_amount,
          (r as any)?.package_count,
        ]
          .map((x) => normalizeStr(x).toLowerCase())
          .join(' ');
        return terms.every((t) => hay.includes(t));
      });
    }

    return out;
  }, [currencyFilter, invoiceFilter, q, shipmentFilter, summaries]);

  const parentColumns: ColumnType<MatLogInvoiceSummary>[] = useMemo(
    () => [
      {
        title: 'Shipment',
        dataIndex: 'shipment_id',
        key: 'shipment_id',
        width: 150,
        render: (v) => {
          const s = normalizeStr(v);
          return s ? <Text code>{s}</Text> : '';
        },
      },
      { title: 'Invoice No', dataIndex: 'invoice_no', key: 'invoice_no', width: 140, render: (v) => normalizeStr(v) },
      { title: 'Cur', dataIndex: 'currency', key: 'currency', width: 70, render: (v) => normalizeStr(v) },
      {
        title: 'Total Amount',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: 140,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => formatNum(v, 3),
      },
      {
        title: 'Total Qty',
        dataIndex: 'total_quantity',
        key: 'total_quantity',
        width: 120,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => formatNum(v, 3),
      },
      {
        title: 'Packages',
        dataIndex: 'package_count',
        key: 'package_count',
        width: 100,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => (v == null || v === '' ? '' : String(v)),
      },
      {
        title: 'Lines',
        dataIndex: 'line_count',
        key: 'line_count',
        width: 80,
        align: 'right' as const,
        className: 'ant-table-column-align-right',
        render: (v) => (v == null || v === '' ? '' : String(v)),
      },
    ],
    []
  );

  const rowKey = (r: MatLogInvoiceSummary) => {
    const shipmentId = normalizeStr((r as any)?.shipment_id);
    const invoiceNo = normalizeStr((r as any)?.invoice_no);
    const cur = normalizeStr((r as any)?.currency);
    return `${shipmentId}::${invoiceNo}::${cur}`;
  };

  const selectedSummaries = useMemo(() => {
    const byKey = new Map<string, MatLogInvoiceSummary>();
    (filtered || []).forEach((r) => byKey.set(rowKey(r), r));
    return (selectedKeys || []).map((k) => byKey.get(String(k))).filter(Boolean) as MatLogInvoiceSummary[];
  }, [filtered, selectedKeys]);

  useEffect(() => {
    const query = normalizeStr(lineQ);
    if (!query) return;

    const terms = query
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (!terms.length) return;

    let cancelled = false;
    void (async () => {
      try {
        const andParts = terms.map((t) => {
          const esc = t.replace(/'/g, "''");
          const like = `%${esc}%`;
          return `(
            coalesce(parent_item_code, '') ilike '${like}'
            or coalesce(itemcode, '') ilike '${like}'
            or coalesce(description, '') ilike '${like}'
          )`;
        });

        const sql = `select distinct shipment_id, invoice_no, currency from public.vw_mat_log_invoice_lines where ${andParts.join(' and ')}`;
        const resp = await fetchSqlDataWithQuery(sql);
        const rows: Array<{ shipment_id?: any; invoice_no?: any; currency?: any }> = unwrapRows(resp);
        if (cancelled) return;

        const matched = new Set(
          (rows || []).map((r) => {
            const shipmentId = normalizeStr((r as any)?.shipment_id);
            const invoiceNo = normalizeStr((r as any)?.invoice_no);
            const cur = normalizeStr((r as any)?.currency);
            return `${shipmentId}::${invoiceNo}::${cur}`;
          })
        );

        const allowed = new Set((filtered || []).map((r) => rowKey(r)));
        const next = Array.from(matched).filter((k) => allowed.has(k));
        setExpandedKeys(next);
      } catch {
        if (cancelled) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filtered, lineQ]);

  const exportSelected = async () => {
    const selected = selectedSummaries || [];
    if (!selected.length) {
      message.info('Select at least one invoice');
      return;
    }

    try {
      setExporting(true);
      const clauses = selected
        .map((r) => {
          const sid = normalizeStr((r as any)?.shipment_id).replace(/'/g, "''");
          const inv = normalizeStr((r as any)?.invoice_no).replace(/'/g, "''");
          const cur = normalizeStr((r as any)?.currency).replace(/'/g, "''");
          return `(
            btrim(shipment_id) = btrim('${sid}')
            and btrim(coalesce(invoice_no, '')) = '${inv}'
            and btrim(coalesce(currency, '')) = '${cur}'
          )`;
        })
        .filter((c) => normalizeStr(c) !== '');

      if (!clauses.length) {
        message.warning('Nothing to export');
        return;
      }

      const sql = `select * from public.vw_mat_log_invoice_lines where ${clauses.join(' or ')} order by shipment_id, invoice_no, currency, itemcode nulls last, unit_price nulls last`;
      const resp = await fetchSqlDataWithQuery(sql);
      const rows: MatLogInvoiceLine[] = unwrapRows<MatLogInvoiceLine>(resp);

      if (!rows.length) {
        message.warning('No invoice line items found for selected invoice(s)');
        return;
      }

      const columns = [
        { key: 'shipment_id', title: 'Shipment' },
        { key: 'invoice_no', title: 'Invoice No' },
        { key: 'currency', title: 'Currency' },
        { key: 'parent_item_code', title: 'Parent' },
        { key: 'itemcode', title: 'Item Code' },
        { key: 'description', title: 'Description' },
        { key: 'unit', title: 'Unit' },
        { key: 'unit_price', title: 'Unit Price' },
        { key: 'quantity', title: 'Qty' },
        { key: 'amount', title: 'Amount' },
        { key: 'package_count', title: 'Package Count' },
      ];

      const exportData = rows.map((row: any) => {
        const obj: any = {};
        columns.forEach((col) => {
          obj[col.title || col.key] = row[col.key];
        });
        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const fileName = selected.length === 1
        ? `mat_log_invoice_${normalizeStr((selected[0] as any)?.shipment_id)}_${normalizeStr((selected[0] as any)?.invoice_no)}_${normalizeStr((selected[0] as any)?.currency)}.xlsx`
        : `mat_log_invoices_selected_${selected.length}.xlsx`;
      saveAs(blob, fileName);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={4}>
            <Select
              allowClear
              showSearch
              placeholder="Shipment"
              value={shipmentFilter as any}
              onChange={(v) => setShipmentFilter((v as any) || undefined)}
              options={shipmentOptions}
              filterOption={(input, option) =>
                String(option?.label || '').toLowerCase().includes(String(input || '').toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              allowClear
              showSearch
              placeholder="Invoice No"
              value={invoiceFilter as any}
              onChange={(v) => setInvoiceFilter((v as any) || undefined)}
              options={invoiceOptions}
              filterOption={(input, option) =>
                String(option?.label || '').toLowerCase().includes(String(input || '').toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              allowClear
              placeholder="Currency"
              value={currencyFilter as any}
              onChange={(v) => setCurrencyFilter((v as any) || undefined)}
              options={currencyOptions}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={5}>
            <Input allowClear placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          </Col>
          <Col xs={24} md={5}>
            <Input
              allowClear
              placeholder="Search lines"
              value={lineQ}
              onChange={(e) => setLineQ(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Space wrap>
              <Button
                onClick={() => {
                  const keys = (filtered || []).map((r) => rowKey(r));
                  setExpandedKeys(keys);
                }}
              >
                Expand all
              </Button>
              <Button onClick={() => setExpandedKeys([])}>Collapse all</Button>
              <Button
                onClick={async () => {
                  await refetch?.();
                  await queryClient.invalidateQueries({ queryKey: ['vw_mat_log_invoice_lines'] });
                }}
                loading={isLoading}
              >
                Reload
              </Button>
              <Button type="primary" onClick={exportSelected} loading={exporting} disabled={!selectedKeys.length}>
                Export to Excel
              </Button>
            </Space>
          </Col>
        </Row>

        {error && <Alert type="error" message="Failed to load invoices" showIcon />}

        <Spin spinning={isLoading}>
          <StandardTable
            columns={parentColumns as any}
            dataSource={filtered}
            rowKey={rowKey as any}
            rowSelection={{
              selectedRowKeys: selectedKeys as any,
              onChange: (keys) => setSelectedKeys((keys as any[]).map((k) => String(k))),
            }}
            size="small"
            style={{ width: '100%' }}
            scroll={{ x: 'max-content' }}
            expandable={{
              columnWidth: 32,
              indentSize: 32,
              expandedRowKeys: expandedKeys as any,
              onExpandedRowsChange: (keys: any) => setExpandedKeys((keys as any[]).map((k) => String(k))),
              expandedRowRender: (rec: any) => {
                const shipmentId = normalizeStr((rec as any)?.shipment_id);
                const invoiceNoRaw = normalizeStr((rec as any)?.invoice_no);
                const currencyRaw = normalizeStr((rec as any)?.currency);
                const invoiceNo = invoiceNoRaw ? invoiceNoRaw : null;
                const currency = currencyRaw ? currencyRaw : null;
                if (!shipmentId) return null;
                return (
                  <InvoiceLinesTable
                    key={`${shipmentId}::${normalizeStr(invoiceNo)}::${normalizeStr(currency)}::${normalizeStr(lineQ)}`}
                    shipmentId={shipmentId}
                    invoiceNo={invoiceNo}
                    currency={currency}
                    lineSearch={lineQ}
                  />
                );
              },
            }}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100', '200'] }}
          />
        </Spin>
      </Space>
    </Card>
  );
};

export default MatLogInvoices;
